import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from '@azure/msal-angular';
import {InteractionType, IPublicClientApplication, PublicClientApplication} from '@azure/msal-browser';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {XAuthorizationInterceptor} from '../http-interceptors/x-authorization.interceptor';
import {DEFAULT_RUNTIME_AUTH_CONFIG, RUNTIME_AUTH_CONFIG, RuntimeAuthConfig} from './runtime-auth-config';

export function apiScopes(clientId: string): string[] {
  return [`api://${clientId}/access_api`];
}

export function MSALInstanceFactory(auth: RuntimeAuthConfig): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: auth.clientId,
      authority: `https://login.microsoftonline.com/${auth.tenantId}`,
      // Web App hosts need this SPA redirect registered on the Entra app:
      // https://wa-eus2-qa-idealcm.azurewebsites.net/redirect
      // https://wa-eus2-prod-idealcm.azurewebsites.net/redirect
      redirectUri: window.location.origin + '/redirect',
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  });
}

// Scopes for Microsoft Graph (user profile, photo, etc.)
export const GRAPH_SCOPES = ['user.read'];
// Scopes for the iDeal API — must be the ONLY resource scopes sent to microservices.
// Mixing Graph scopes here causes MSAL to send a Graph token to the API, which
// fails AAD signature validation (IDX10511).
export const API_SCOPES = apiScopes(DEFAULT_RUNTIME_AUTH_CONFIG.clientId);

export function MSALGuardConfigFactory(auth: RuntimeAuthConfig): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...GRAPH_SCOPES, ...apiScopes(auth.clientId)]
    },
    loginFailedRoute: '/403',
  };
}

export function MSALInterceptorConfigFactory(auth: RuntimeAuthConfig): MsalInterceptorConfiguration {
  const scopes = apiScopes(auth.clientId);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', GRAPH_SCOPES],
      ['/api/**', scopes],
    ]),
  };
}

export const AUTH_PROVIDERS = [
  {provide: RUNTIME_AUTH_CONFIG, useValue: DEFAULT_RUNTIME_AUTH_CONFIG},
  {provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory, deps: [RUNTIME_AUTH_CONFIG]},
  {provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory, deps: [RUNTIME_AUTH_CONFIG]},
  {provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory, deps: [RUNTIME_AUTH_CONFIG]},
  MsalGuard,
  MsalBroadcastService,
  MsalService,
  {provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true},
  // Must be registered AFTER MsalInterceptor so it runs innermost and sees
  // the Authorization header that MSAL set before renaming it to X-Authorization.
  {provide: HTTP_INTERCEPTORS, useClass: XAuthorizationInterceptor, multi: true},
]
